import React from "react";
import { Card, CardBody, Nav, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import {
    setAdvancedDate,
    setAdvancedLegend,
    setAdvancedTypeParam,
    setSegmentQueryString,
} from "@pages/Statistics/Transactions/store";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    getAdvancedPeriodStringParam,
    getAdvancedSeeMorePeriod,
    getFamiliesFilterQueryString,
    getFormattedCategoriesByPeriod,
    getStatisticsData,
    getTypeParam,
    getUntranslatedString,
} from "@helpers/general";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";

import ChartWrapper from "@pages/Statistics/CommonCharts/ChartWrapper";

import "@assets/apex-chart.css";

export default function StatisticsSalesSupportCol() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const {
        period,
        type,
        advancedDate,
        selections,
        setHistogramData,
        setCategoriesData,
        salesSupportSelection,
    } = React.useContext(StatisticsContext);

    const [categories, setCategories] = React.useState<string[]>([]);
    const [top10SubFamiliesData, setTop10SubFamiliesData] = React.useState<
        any[]
    >([]);

    const {
        resourceData: statisticsSubFamiliesSalesSupportData,
        error: statisticsSubFamiliesSalesSupportDataError,
    } = useResource(
        process.env.REACT_APP_API_V1_URL +
            `/statistics/sub-families/sales-support?type=${getTypeParam(
                type
            )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
                selections.designation &&
                selections.designation !== "my_favorites"
                    ? `&selected=${selections.designation}`
                    : ""
            }${
                getFamiliesFilterQueryString(salesSupportSelection!) !== ""
                    ? `&sales-support=${getFamiliesFilterQueryString(
                          salesSupportSelection!
                      ).toUpperCase()}`
                    : ""
            }`
    );

    React.useEffect(() => {
        setCategories(getFormattedCategoriesByPeriod(period, advancedDate));
        setCategoriesData!(
            getFormattedCategoriesByPeriod(period, advancedDate)
        );
    }, [period, advancedDate, setCategoriesData]);

    React.useEffect(() => {
        setHistogramData!(statisticsSubFamiliesSalesSupportData.data);
        setTop10SubFamiliesData(
            getStatisticsData(
                statisticsSubFamiliesSalesSupportData.data,
                categories,
                period,
                advancedDate
            )
        );
    }, [
        statisticsSubFamiliesSalesSupportData,
        categories,
        period,
        advancedDate,
        t,
        setHistogramData,
    ]);

    function handleSeeMoreButtonOnClickEvent() {
        let segmentObject = {
            designation: "temp",
            value: [
                {
                    designation: "subfamilies",
                    value: top10SubFamiliesData.map(
                        (subFamily) => subFamily.name
                    ),
                },
                {
                    designation: "sales_support",
                    value: salesSupportSelection!
                        .filter(
                            (mode: string) => mode !== t("All sales supports")
                        )
                        .map((item: string) => {
                            return getUntranslatedString(item).toUpperCase();
                        }),
                },
            ],
        };

        setAdvancedLegend(t("Sub-families"));
        setAdvancedTypeParam(type);
        setSegmentQueryString(encodeURI(JSON.stringify(segmentObject)));

        setAdvancedDate(getAdvancedSeeMorePeriod(period, advancedDate));

        history.push("/statistics-advanced", { prevUrl: location.pathname });
    }

    return (
        <Card>
            <CardBody className="card-body__clz card-body__clz-sm">
                <Nav pills className="navtab-bg float-right d-none">
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={handleSeeMoreButtonOnClickEvent}
                    >
                        {t("See more")}{" "}
                        <i className="mdi mdi-arrow-right ml-1"></i>
                    </Button>
                </Nav>
                <CardBody className="pb-0 chart-order chart-order-sm chart-order-md chart-order-lg chart-order-xl">
                    <div className="apex-charts apex-chart__clz">
                        {statisticsSubFamiliesSalesSupportDataError ? (
                            <ErrorPage />
                        ) : (
                            <CustomErrorBoundary>
                                <ChartWrapper
                                    series={top10SubFamiliesData}
                                    categories={categories}
                                />
                            </CustomErrorBoundary>
                        )}
                    </div>
                </CardBody>
            </CardBody>
        </Card>
    );
}
